import { sampleEffectivePosition, sampleTrackPosition, isPlayerTrackId } from './sampling';

const angleDelta = (from, to) => Math.atan2(Math.sin(to - from), Math.cos(to - from));

// Sample the path, not last frame's displacement: timeline and render frames
// may arrive at different rates, and scrubbing backwards must still face forward.
//
// `allTracks` makes one extra case possible: a player FOLLOWING another player
// moves by their own keys PLUS the leader's travel, so their body has to turn
// with the combined path. Facing the authored path alone would have a shadow
// running sideways whenever its leader turned.
export function travelYaw(track, time, allTracks) {
    const keys = track?.position || [];
    const attach = isPlayerTrackId(track?.id)
        ? (track?.attach || []).filter(k => k && typeof k.time === 'number').sort((a, b) => a.time - b.time)
        : [];
    const marking = attach.length > 0;
    if (keys.length < 2 && !marking) return null;
    if (!keys.length && !marking) return null;

    // Which way this player is being moved RIGHT NOW, and how far that answer
    // holds. A mark parks their own keys: from the mark key until it is
    // released — or until the scene ends, if it never is — the LEADER'S travel
    // is what moves them. Two mistakes lived here at once. The window used to
    // stop at the mark key, so a marker whose authored path ended there had no
    // direction at all and kept the heading from before the mark; and it
    // reached ACROSS the mark key, so the step from the authored path to the
    // marking spot — a teleport, not a run — was read as the direction of
    // travel and pointed the body at wherever the gap happened to be.
    const activeMark = marking ? attach.filter(k => k.time <= time + 1e-6).pop() : null;
    const holds = !!(activeMark && activeMark.value && activeMark.value !== 'none');
    const ownTimes = keys.map(k => k.time);
    let lo, hi;
    if (holds) {
        lo = activeMark.time;
        const next = attach.find(k => k.time > activeMark.time + 1e-9);
        hi = next ? next.time : Infinity;
    } else {
        if (ownTimes.length < 2) return null;
        lo = Math.min(...ownTimes);
        hi = Math.max(...ownTimes);
        // A mark that has not started yet is a discontinuity ahead of them,
        // not a corner to turn at.
        const nextMark = attach.find(k => k.time > time + 1e-9);
        if (nextMark) hi = Math.min(hi, nextMark.time - 1e-6);
    }
    if (time < lo || time > hi) return null;
    const at = (tt) => (marking
        ? sampleEffectivePosition(track, tt, allTracks) : sampleTrackPosition(track, tt));
    const a = at(Math.max(lo, time - 0.04));
    const b = at(Math.min(hi, time + 0.04));
    if (!a || !b) return null;
    const dx = b[0] - a[0], dz = b[2] - a[2];
    return Math.hypot(dx, dz) > 1e-4 ? Math.atan2(dx, dz) : null;
}

export function targetYaw(pos, target, fallback, isBall = false) {
    if (!target) return fallback;
    const dx = target[0] - pos[0], dz = target[2] - pos[2];
    const distance = Math.hypot(dx, dz);
    // Near a player's feet, a few centimetres can reverse the look direction.
    // Keep the travel heading (or held heading at rest) through this region.
    if (distance < (isBall ? 0.9 : 0.05)) return fallback;
    const yaw = Math.atan2(dx, dz);
    if (!isBall) return yaw;
    const blend = Math.min(1, (distance - 0.9) / 1.3);
    const weight = blend * blend * (3 - 2 * blend);
    return fallback + angleDelta(fallback, yaw) * weight;
}

/**
 * Which way a player's body points: the direction they are TRAVELLING.
 *
 * There used to be a second answer — a look-at TARGET: a pinned point on the
 * grass, or a gaze locked on the ball or a teammate. It is gone. It needed a
 * red pin nobody could find, a keyboard chord, and a mode no button could
 * enter, and it silently overrode the running direction that is the entire
 * point of a movement diagram.
 *
 * What stays is the one rule: face where you are going. A player with no path
 * to follow (standing still, or never moved) keeps the heading they already
 * hold; a player who has never had one glances at the nearest ball, because
 * that is what a stationary player on a pitch is always doing.
 */
export function playerFacingYaw({ tracks, playerId, pos, time, currentYaw }) {
    const pathYaw = travelYaw(tracks.find(track => track.id === playerId), time, tracks);
    if (pathYaw !== null) return pathYaw;
    const fallback = currentYaw;
    let target = null;
    let nearest = Infinity;
    for (const track of tracks) {
        if (!(track.type === 'ball' || track.id?.startsWith('ball:'))) continue;
        const p = sampleEffectivePosition(track, time, tracks);
        if (!p) continue;
        const distance = Math.hypot(p[0] - pos[0], p[2] - pos[2]);
        if (distance < nearest) { nearest = distance; target = track; }
    }
    if (!target) return fallback;
    const isBall = target.type === 'ball' || target.id?.startsWith('ball:');
    if (isBall) {
        const active = [...(target.attach || [])].sort((a, b) => a.time - b.time).filter(k => k.time <= time).pop();
        // A carried ball's lead offset is derived from the carrier's heading.
        // Do not feed that offset back into the carrier's body rotation.
        const next = [...(target.attach || [])].sort((a, b) => a.time - b.time).find(k => k.time > time);
        const leaving = next && next.value !== active?.value;
        if (active?.value === playerId && !leaving) return fallback;
    }
    return targetYaw(pos, sampleEffectivePosition(target, time, tracks), fallback, isBall);
}

export function smoothPlayerYaw(previous, target, time, dt) {
    // A seek or loop restart should show the new heading immediately, without
    // dragging rotational history from another part of the scenario.
    if (!previous || time < previous.time || time - previous.time > 0.25) return { yaw: target, time };
    const weight = 1 - Math.exp(-Math.max(0, dt) / 0.16);
    return { yaw: previous.yaw + angleDelta(previous.yaw, target) * weight, time };
}
